import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import { FiSmartphone, FiExternalLink, FiShield, FiZap } from 'react-icons/fi';
import { motion } from 'framer-motion';

const TaxPortal = () => {
  const { t, language } = useLanguage();
  const portalUrl = "https://albertorodriguez.mytaxportal.online/";
  const qrUrl = "https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/4cccfcea5b12263d4cf57505131a66d2.png";

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 rounded-[2.5rem] shadow-2xl border border-blue-700/30"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl"></div>

          <div className="relative z-10 px-8 py-12 md:p-16 lg:flex lg:items-center lg:justify-between gap-12">
            <div className="lg:max-w-xl mb-12 lg:mb-0">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-sm font-bold mb-6">
                <SafeIcon icon={FiZap} className="w-4 h-4" />
                <span>{language === 'es' ? 'ACCESO INSTANTÁNEO' : 'INSTANT ACCESS'}</span>
              </div>
              
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                {language === 'es' 
                  ? 'Tu Portal de Taxes en la Palma de tu Mano' 
                  : 'Your Tax Portal in the Palm of Your Hand'}
              </h2>
              
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                {language === 'es'
                  ? 'Reporta tus taxes, sube documentos y revisa tu estado de forma segura y rápida desde cualquier lugar.'
                  : 'Report your taxes, upload documents, and check your status securely and quickly from anywhere.'}
              </p>

              <div className="space-y-4 mb-10">
                {[
                  { icon: FiShield, es: 'Seguridad de nivel bancario', en: 'Bank-level security' },
                  { icon: FiSmartphone, es: 'Optimizado para tu celular', en: 'Mobile-friendly design' },
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 text-blue-200">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <SafeIcon icon={feature.icon} className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-medium">{language === 'es' ? feature.es : feature.en}</span>
                  </div>
                ))}
              </div>

              <a 
                href={portalUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-900 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl active:scale-95"
              >
                <span>{t('services.taxes.portalBtn')}</span>
                <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
              </a>
            </div>

            {/* QR Container */}
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className="relative group">
                {/* Glow effect */}
                <div className="absolute -inset-4 bg-white/20 rounded-[2rem] blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative bg-white p-6 md:p-8 rounded-[2rem] shadow-2xl border border-white/20">
                  <img 
                    src={qrUrl} 
                    alt="Tax Portal QR" 
                    className="w-48 h-48 md:w-64 md:h-64 object-contain mix-blend-multiply"
                  />
                  <div className="mt-6 text-center">
                    <p className="text-blue-900 font-black text-sm uppercase tracking-widest mb-1">
                      {t('services.taxes.scanText')}
                    </p>
                    <p className="text-gray-400 text-[10px] font-bold">
                      {language === 'es' ? 'ABRE TU CÁMARA AHORA' : 'OPEN YOUR CAMERA NOW'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TaxPortal;